// frontend/src/components/Portfolio/TCFDReporting.tsx
// TCFD Climate Disclosure Reporting Component
// Follows TCFD framework (now incorporated into IFRS S2)

import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Alert,
  Divider,
  Stack,
} from '@mui/material';
import {
  ExpandMore,
  Download,
  AccountBalance,
  Timeline,
  Assessment,
  Security,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from 'recharts';

interface PortfolioItem {
  postal_code?: string;
  asset_value: number | string;
  risk_score?: number;
  top_physical_risk?: string;
  flood?: number;
  wildfire?: number;
  hurricane?: number;
  earthquake?: number;
  heat?: number;
  drought?: number;
  [key: string]: any;
}

interface TCFDReportingProps {
  portfolio: {
    items: PortfolioItem[];
    name?: string;
    totalValue?: number;
    total_value?: number;
    [key: string]: any;
  };
}

const COLORS = {
  primary: '#1976d2',
  success: '#2e7d32',
  warning: '#ed6c02',
  error: '#d32f2f',
};

export default function TCFDReporting({ portfolio }: TCFDReportingProps) {
  const [activeTab, setActiveTab] = useState(0);

  // Calculate metrics
  const metrics = useMemo(() => {
    const items = portfolio.items || [];
    const totalValue = portfolio.totalValue || portfolio.total_value || 0;
    
    // Physical Risk Assessment
    const physicalRisks = {
      acute: items.filter(item => {
        const topRisk = item.top_physical_risk?.toLowerCase();
        return topRisk && ['hurricane', 'flood', 'wildfire', 'tornado'].includes(topRisk);
      }).length,
      chronic: items.filter(item => {
        const topRisk = item.top_physical_risk?.toLowerCase();
        return topRisk && ['heat', 'drought', 'sea_level'].includes(topRisk);
      }).length,
    };

    // Risk calculations
    const avgRisk = items.reduce((sum, item) => sum + (item.risk_score || 0), 0) / items.length || 0;
    const highRiskAssets = items.filter(item => (item.risk_score || 0) > 6);
    const valueAtRisk = highRiskAssets.reduce((sum, item) => {
      const value = typeof item.asset_value === 'string' 
        ? parseFloat(item.asset_value.replace(/[$,]/g, '')) 
        : item.asset_value || 0;
      return sum + value;
    }, 0);

    // Climate scenario impacts (IPCC AR6)
    const scenarios = {
      ssp126: { warming: '1.5-2.0°C', impactMultiplier: 1.2 },
      ssp245: { warming: '2.5-3.0°C', impactMultiplier: 1.4 },
      ssp585: { warming: '4.0-5.0°C', impactMultiplier: 1.62 },
    };

    const scenarioImpacts = Object.entries(scenarios).map(([key, scenario]) => ({
      scenario: key.toUpperCase().replace('SSP', 'SSP'),
      warming: scenario.warming,
      projectedLoss: totalValue * (avgRisk / 100) * scenario.impactMultiplier,
      assetsAtRisk: Math.round(items.length * (avgRisk / 10) * (scenario.impactMultiplier / 1.2)),
    }));

    // All 18 hazard types
    const ALL_HAZARDS = [
      'avalanche', 'flood', 'flood_r', 'cold', 'drought', 'earthquake',
      'hail', 'heat', 'hurricane', 'ice', 'landslide', 'lightning',
      'tornado', 'tsunami', 'volcanic', 'wildfire', 'wind', 'winter'
    ];

    const HAZARD_LABELS: { [key: string]: string } = {
      avalanche: 'Avalanche',
      flood: 'Coastal Flood',
      flood_r: 'River Flood',
      cold: 'Cold Wave',
      drought: 'Drought',
      earthquake: 'Earthquake',
      hail: 'Hail',
      heat: 'Heat Wave',
      hurricane: 'Hurricane',
      ice: 'Ice Storm',
      landslide: 'Landslide',
      lightning: 'Lightning',
      tornado: 'Tornado',
      tsunami: 'Tsunami',
      volcanic: 'Volcanic Activity',
      wildfire: 'Wildfire',
      wind: 'Strong Wind',
      winter: 'Winter Storm'
    };

    // Risk by all 18 hazard types
    const hazardBreakdown = ALL_HAZARDS.map(hazard => {
      const affectedItems = items.filter(item => (item[hazard] || 0) > 20);
      const hazardValue = affectedItems.reduce((sum, item) => {
        const value = typeof item.asset_value === 'string'
          ? parseFloat(item.asset_value.replace(/[$,]/g, ''))
          : item.asset_value || 0;
        return sum + value;
      }, 0);
      
      return {
        name: HAZARD_LABELS[hazard],
        hazardKey: hazard,
        count: affectedItems.length,
        value: hazardValue,
        avgRisk: affectedItems.length > 0
          ? affectedItems.reduce((sum, item) => sum + (item[hazard] || 0), 0) / affectedItems.length
          : 0,
      };
    }).filter(h => h.count > 0);

    return {
      totalAssets: items.length,
      totalValue,
      avgRisk,
      valueAtRisk,
      physicalRisks,
      scenarioImpacts,
      hazardBreakdown,
    };
  }, [portfolio]);

  // TCFD Governance Section
  const GovernanceSection = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountBalance color="primary" />
        Governance
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Board oversight and management's role in assessing climate-related risks and opportunities
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Board Oversight
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" fontWeight="medium">Risk Committee</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Reviews climate risk assessments quarterly
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight="medium">Strategic Review</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Climate scenarios inform annual strategic planning
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight="medium">Reporting</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Regular updates on portfolio climate exposure
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Management's Role
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" fontWeight="medium">Risk Assessment</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Continuous monitoring of {metrics.totalAssets} assets
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight="medium">Integration</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Climate risk integrated into investment decisions
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight="medium">Performance</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Climate metrics tied to management objectives
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // TCFD Strategy Section
  const StrategySection = () => {
    // Limit radar chart to top 10 hazards for readability
    const topHazards = [...metrics.hazardBreakdown]
      .sort((a, b) => b.avgRisk - a.avgRisk)
      .slice(0, 10);

    const radarData = topHazards.map(h => ({
      hazard: h.name,
      risk: h.avgRisk,
      fullMark: 100,
    }));

    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Timeline color="primary" />
          Strategy
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Climate-related risks and opportunities impacting business strategy and financial planning
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Physical Risk Exposure (Top 10 Hazards)
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer>
                    <RadarChart data={radarData}>
                      <PolarGrid />
                      <PolarAngleAxis 
                        dataKey="hazard" 
                        tick={{ fontSize: 10 }}
                      />
                      <PolarRadiusAxis angle={90} domain={[0, 100]} />
                      <Radar
                        name="Risk Level"
                        dataKey="risk"
                        stroke={COLORS.primary}
                        fill={COLORS.primary}
                        fillOpacity={0.6}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Showing top 10 of {metrics.hazardBreakdown.length} hazards by risk level
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Risk Distribution
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2">Acute Physical Risks</Typography>
                      <Chip 
                        label={metrics.physicalRisks.acute} 
                        size="small" 
                        color="error"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Hurricanes, floods, wildfires, severe storms
                    </Typography>
                  </Box>
                  <Box>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2">Chronic Physical Risks</Typography>
                      <Chip 
                        label={metrics.physicalRisks.chronic} 
                        size="small" 
                        color="warning"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Heat stress, drought, sea level rise
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      Value at Risk (High Risk Assets)
                    </Typography>
                    <Typography variant="h5" color="error.main">
                      ${(metrics.valueAtRisk / 1e6).toFixed(1)}M
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {((metrics.valueAtRisk / metrics.totalValue) * 100).toFixed(1)}% of total portfolio value
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Climate Scenario Analysis (IPCC AR6)
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Scenario</strong></TableCell>
                        <TableCell><strong>Warming by 2100</strong></TableCell>
                        <TableCell align="right"><strong>Assets at Risk</strong></TableCell>
                        <TableCell align="right"><strong>Projected Loss (10yr)</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {metrics.scenarioImpacts.map((scenario, index) => (
                        <TableRow key={scenario.scenario}>
                          <TableCell>
                            <Chip 
                              label={scenario.scenario}
                              size="small"
                              color={index === 0 ? 'success' : index === 1 ? 'warning' : 'error'}
                            />
                          </TableCell>
                          <TableCell>{scenario.warming}</TableCell>
                          <TableCell align="right">{scenario.assetsAtRisk}</TableCell>
                          <TableCell align="right">
                            ${(scenario.projectedLoss / 1e6).toFixed(2)}M
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="caption">
                    <strong>SSP1-2.6:</strong> Strong mitigation, low emissions pathway
                    <br />
                    <strong>SSP2-4.5:</strong> Middle-of-the-road, current policies
                    <br />
                    <strong>SSP5-8.5:</strong> High emissions, fossil fuel intensive
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // TCFD Risk Management Section
  const RiskManagementSection = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Security color="primary" />
        Risk Management
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Processes for identifying, assessing, and managing climate-related risks
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="primary">
                1. Identification
              </Typography>
              <Typography variant="body2">
                • Geographic risk mapping
                <br />
                • Multi-hazard assessment
                <br />
                • Forward-looking scenarios
                <br />
                • Stakeholder engagement
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="primary">
                2. Assessment
              </Typography>
              <Typography variant="body2">
                • Quantitative risk scoring
                <br />
                • Financial impact modeling
                <br />
                • Time horizon analysis
                <br />
                • Materiality evaluation
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="primary">
                3. Management
              </Typography>
              <Typography variant="body2">
                • Risk mitigation strategies
                <br />
                • Portfolio diversification
                <br />
                • Insurance optimization
                <br />
                • Continuous monitoring
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                Comprehensive Risk Assessment Matrix - All 18 Climate Hazards
              </Typography>
              <TableContainer sx={{ maxHeight: 500 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Hazard Type</strong></TableCell>
                      <TableCell align="right"><strong>Assets Exposed</strong></TableCell>
                      <TableCell align="right"><strong>Value Exposed</strong></TableCell>
                      <TableCell align="right"><strong>Avg Risk Score</strong></TableCell>
                      <TableCell><strong>Severity</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metrics.hazardBreakdown
                      .sort((a, b) => b.avgRisk - a.avgRisk)
                      .map((hazard) => (
                      <TableRow key={hazard.hazardKey}>
                        <TableCell>{hazard.name}</TableCell>
                        <TableCell align="right">{hazard.count}</TableCell>
                        <TableCell align="right">
                          ${(hazard.value / 1e6).toFixed(1)}M
                        </TableCell>
                        <TableCell align="right">{hazard.avgRisk.toFixed(1)}</TableCell>
                        <TableCell>
                          <Chip
                            label={hazard.avgRisk > 60 ? 'High' : hazard.avgRisk > 30 ? 'Medium' : 'Low'}
                            size="small"
                            color={hazard.avgRisk > 60 ? 'error' : hazard.avgRisk > 30 ? 'warning' : 'success'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  <strong>Coverage:</strong> Analysis includes all 18 climate hazards tracked in the portfolio. 
                  Only hazards with exposure (risk score {'>'} 20) are shown.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  // TCFD Metrics & Targets Section
  const MetricsSection = () => {
    const timeSeriesData = [0, 2, 4, 6, 8, 10].map(year => ({
      year: year === 0 ? 'Now' : `Year ${year}`,
      conservative: metrics.valueAtRisk * (1 + (0.20 * year / 10)) / 1e6,
      moderate: metrics.valueAtRisk * (1 + (0.40 * year / 10)) / 1e6,
      severe: metrics.valueAtRisk * (1 + (0.62 * year / 10)) / 1e6,
    }));

    return (
      <Box>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assessment color="primary" />
          Metrics & Targets
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Metrics used to assess and manage climate-related risks and opportunities
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Portfolio Climate Risk
                </Typography>
                <Typography variant="h4" color="primary">
                  {metrics.avgRisk.toFixed(1)}
                </Typography>
                <Typography variant="caption">
                  Average Risk Score (0-10)
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  High Risk Assets
                </Typography>
                <Typography variant="h4" color="error">
                  {metrics.hazardBreakdown.reduce((sum, h) => sum + h.count, 0)}
                </Typography>
                <Typography variant="caption">
                  {((metrics.hazardBreakdown.reduce((sum, h) => sum + h.count, 0) / metrics.totalAssets) * 100).toFixed(1)}% of portfolio
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Value at Risk
                </Typography>
                <Typography variant="h4" color="warning.main">
                  ${(metrics.valueAtRisk / 1e6).toFixed(1)}M
                </Typography>
                <Typography variant="caption">
                  High risk exposure
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Risk Reduction Target
                </Typography>
                <Typography variant="h4" color="success.main">
                  -25%
                </Typography>
                <Typography variant="caption">
                  By 2030
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Projected Value at Risk - 10 Year Outlook
                </Typography>
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer>
                    <LineChart data={timeSeriesData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="year" />
                      <YAxis label={{ value: 'Value at Risk ($M)', angle: -90, position: 'insideLeft' }} />
                      <RechartsTooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="conservative"
                        stroke="#2e7d32"
                        strokeWidth={2}
                        name="SSP1-2.6 (Conservative)"
                      />
                      <Line
                        type="monotone"
                        dataKey="moderate"
                        stroke="#ed6c02"
                        strokeWidth={2}
                        name="SSP2-4.5 (Moderate)"
                      />
                      <Line
                        type="monotone"
                        dataKey="severe"
                        stroke="#d32f2f"
                        strokeWidth={2}
                        name="SSP5-8.5 (Severe)"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Climate Risk Reduction Targets
                </Typography>
                <Stack spacing={2}>
                  <Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">Reduce High-Risk Asset Exposure</Typography>
                      <Typography variant="body2" fontWeight="bold">25% by 2030</Typography>
                    </Box>
                    <Box sx={{ bgcolor: '#e0e0e0', height: 8, borderRadius: 4 }}>
                      <Box sx={{ bgcolor: COLORS.success, width: '15%', height: 8, borderRadius: 4 }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      15% progress toward target
                    </Typography>
                  </Box>

                  <Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">Diversify Geographic Risk</Typography>
                      <Typography variant="body2" fontWeight="bold">50% reduction in concentration</Typography>
                    </Box>
                    <Box sx={{ bgcolor: '#e0e0e0', height: 8, borderRadius: 4 }}>
                      <Box sx={{ bgcolor: COLORS.warning, width: '30%', height: 8, borderRadius: 4 }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      30% progress toward target
                    </Typography>
                  </Box>

                  <Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2">Implement Physical Risk Mitigation</Typography>
                      <Typography variant="body2" fontWeight="bold">100% of high-risk assets</Typography>
                    </Box>
                    <Box sx={{ bgcolor: '#e0e0e0', height: 8, borderRadius: 4 }}>
                      <Box sx={{ bgcolor: COLORS.primary, width: '45%', height: 8, borderRadius: 4 }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      45% progress toward target
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const exportTCFDReport = () => {
    const report = {
      portfolioName: portfolio.name || 'Portfolio',
      generatedDate: new Date().toISOString(),
      governance: {
        boardOversight: 'Quarterly risk committee reviews',
        managementRole: 'Continuous monitoring and integration',
      },
      strategy: {
        physicalRisks: metrics.physicalRisks,
        valueAtRisk: metrics.valueAtRisk,
        scenarios: metrics.scenarioImpacts,
      },
      riskManagement: {
        hazardBreakdown: metrics.hazardBreakdown,
        assessmentFramework: 'Multi-hazard quantitative analysis',
      },
      metrics: {
        totalAssets: metrics.totalAssets,
        totalValue: metrics.totalValue,
        averageRisk: metrics.avgRisk,
        targets: {
          riskReduction: '25% by 2030',
          geographicDiversification: '50% reduction in concentration',
          mitigation: '100% high-risk assets',
        },
      },
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `TCFD-Report-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            TCFD Climate Disclosure Report
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Task Force on Climate-related Financial Disclosures
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={exportTCFDReport}
        >
          Export Report
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          This report follows the TCFD framework, now incorporated into IFRS S2 Climate-related Disclosures. 
          Data reflects IPCC AR6 scenarios and scientifically-vetted climate projections.
        </Typography>
      </Alert>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} variant="fullWidth">
          <Tab label="Governance" icon={<AccountBalance />} iconPosition="start" />
          <Tab label="Strategy" icon={<Timeline />} iconPosition="start" />
          <Tab label="Risk Management" icon={<Security />} iconPosition="start" />
          <Tab label="Metrics & Targets" icon={<Assessment />} iconPosition="start" />
        </Tabs>
      </Paper>

      <Box sx={{ mt: 3 }}>
        {activeTab === 0 && <GovernanceSection />}
        {activeTab === 1 && <StrategySection />}
        {activeTab === 2 && <RiskManagementSection />}
        {activeTab === 3 && <MetricsSection />}
      </Box>
    </Box>
  );
}
